import { useEffect } from 'react'
import { pushService } from '@/modules/notifications/services/push.service'
import { ensureNotificationPermission } from '@/modules/notifications/utils/notification-permission'
import { syncFcmDeviceRegistration } from '@/modules/notifications/utils/fcm-lifecycle'
import { useAuthStore } from '@/store/auth.store'
import { isFcmConfigured } from '@/services/fcm'

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(new ArrayBuffer(rawData.length))
  for (let i = 0; i < rawData.length; i += 1) output[i] = rawData.charCodeAt(i)
  return output
}

/**
 * Registers the push service worker and subscribes this browser once per
 * app load — mounted in the authenticated shell (AppLayout), same spot as
 * useNotificationRealtime. Entirely best-effort: unsupported browsers,
 * unconfigured VAPID keys, or a denied permission prompt all just no-op.
 */
export function usePushSubscription() {
  const userId = useAuthStore((s) => s.session?.user.id)

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    let cancelled = false

    async function run() {
      try {
        // Ask early even if VAPID keys are blank — FCM still needs the OS prompt.
        const permission = await ensureNotificationPermission()
        if (permission !== 'granted' || cancelled) return

        // If FCM is also enabled, register token now that permission is granted
        // (covers race where useFcmClient checked before the shared prompt finished).
        if (userId && isFcmConfigured()) {
          void syncFcmDeviceRegistration(userId)
        }

        const { publicKey } = await pushService.getPublicKey()
        if (!publicKey || cancelled) return

        const registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none',
        })
        // Ensure the worker is active before pushManager.subscribe (desktop Chrome).
        if (!registration.active) {
          const worker = registration.installing ?? registration.waiting
          if (worker) {
            await new Promise<void>((resolve) => {
              const onStateChange = () => {
                if (worker.state === 'activated' || worker.state === 'redundant') {
                  worker.removeEventListener('statechange', onStateChange)
                  resolve()
                }
              }
              worker.addEventListener('statechange', onStateChange)
              if (worker.state === 'activated' || worker.state === 'redundant') {
                worker.removeEventListener('statechange', onStateChange)
                resolve()
              }
            })
          }
        }
        await registration.update().catch(() => undefined)

        let subscription = await registration.pushManager.getSubscription()
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          })
        }

        const json = subscription.toJSON()
        if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return

        await pushService.subscribe({
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
          userAgent: navigator.userAgent,
        })
      } catch {
        // Push is a best-effort background channel — never block the app on it.
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [userId])
}
